import React, { useRef, useState, useEffect, useMemo } from "react";
import { useAppContext } from "../../stores/appStore";
import gsap from "gsap";
import "./Rocket.css";

// ✅ FIX 1: Global image cache to prevent recreating 133 images every mount
const IMAGE_CACHE = [];
const preloadImages = () => {
  if (IMAGE_CACHE.length > 0) return IMAGE_CACHE;

  for (let i = 1; i <= 133; i++) {
    const paddedNumber = String(i).padStart(3, "0");
    const img = new Image();
    img.src = `/models/rocket2/${paddedNumber}.png`;
    img.onerror = () => {
      console.warn(`Could not load frame ${paddedNumber}`);
    };
    IMAGE_CACHE.push(img);
  }

  return IMAGE_CACHE;
};

// Booster light fixed to viewport, tracks rocket position
const RocketBoosterLight = React.memo(({ fly, rocketRef }) => {
  const canvasRef = useRef();
  const timeRef = useRef(0);
  const animationFrameRef = useRef();
  const ctxRef = useRef(null);

  const { altitudeAnimationInProgress, prepareDraw, altitude } =
    useAppContext();

  const getIntensity = () => {
    if (!altitudeAnimationInProgress && fly) {
      return 0;
    }
    if (altitudeAnimationInProgress || altitude) {
      return 1.0;
    } else if (prepareDraw) {
      return 1;
    } else {
      return 0;
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (!ctxRef.current) {
      ctxRef.current = canvas.getContext("2d");
    }
    const ctx = ctxRef.current;

    // ✅ FULL VIEWPORT CANVAS
    const updateCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    updateCanvasSize();

    const animate = () => {
      timeRef.current += 0.016;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const intensity = getIntensity();
      if (intensity === 0) {
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }

      // ✅ Calculate rocket booster position in viewport
      let rocketX = canvas.width * 0.67; // Default position
      let rocketY = canvas.height * 0.5;

      if (rocketRef && rocketRef.current) {
        const rect = rocketRef.current.getBoundingClientRect();
        // Position of bottom center of rocket (where boosters are)
        rocketX = rect.left + rect.width / 2;
        rocketY = rect.bottom;
      }

      // ✅✅✅ ASYMMETRIC TRIANGLE - from rocket boosters to viewport bottom-left
      ctx.save();
      ctx.globalAlpha = intensity;

      ctx.beginPath();
      ctx.moveTo(rocketX - 110, rocketY - 470); // Apex at rocket boosters
      ctx.lineTo(0, canvas.height); // Bottom-LEFT corner of viewport
      ctx.lineTo(rocketX - 87, canvas.height); // Almost straight down from rocket
      ctx.closePath();
      ctx.clip();

      const gradient = ctx.createLinearGradient(
        rocketX,
        rocketY,
        rocketX / 2,
        canvas.height
      );
      gradient.addColorStop(0, "rgba(150, 80, 200, 0.7)");
      gradient.addColorStop(0.2, "rgba(140, 70, 190, 0.6)");
      gradient.addColorStop(0.4, "rgba(130, 60, 180, 0.45)");
      gradient.addColorStop(0.7, "rgba(120, 50, 170, 0.25)");
      gradient.addColorStop(1, "rgba(100, 40, 150, 0)");

      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      // Ripple waves
      for (let i = 0; i < 3; i++) {
        ctx.save();
        const waveOffset = (timeRef.current * (2.5 + i * 0.2)) % (Math.PI * 2);
        const waveAlpha = (Math.sin(waveOffset) * 0.5 + 0.5) * 0.3 * intensity;
        ctx.globalAlpha = waveAlpha;

        ctx.beginPath();
        ctx.moveTo(rocketX - 110, rocketY - 470); // Apex at rocket boosters
        ctx.lineTo(0, canvas.height); // Bottom-LEFT corner of viewport
        ctx.lineTo(rocketX - 26, canvas.height); // Almost straight down from rocket
        ctx.closePath();
        ctx.clip();

        const waveGradient = ctx.createLinearGradient(
          rocketX,
          rocketY,
          rocketX / 2,
          canvas.height
        );
        waveGradient.addColorStop(0, "rgba(160, 100, 210, 1)");
        waveGradient.addColorStop(0.5, "rgba(150, 90, 200, 0.5)");
        waveGradient.addColorStop(1, "rgba(130, 70, 180, 0)");

        ctx.fillStyle = waveGradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
      }

      // Outer glow
      ctx.save();
      ctx.globalAlpha = intensity * 0.08;

      ctx.beginPath();
      ctx.moveTo(rocketX - 110, rocketY - 470); // Apex at rocket boosters
      ctx.lineTo(-87, canvas.height);
      ctx.lineTo(rocketX, canvas.height);
      ctx.closePath();
      ctx.clip();

      const glowGradient = ctx.createLinearGradient(
        rocketX,
        rocketY,
        rocketX / 2,
        canvas.height
      );
      glowGradient.addColorStop(0, "rgba(160, 100, 210, 0.3)");
      glowGradient.addColorStop(0.3, "rgba(150, 90, 200, 0.2)");
      glowGradient.addColorStop(0.6, "rgba(140, 80, 190, 0.1)");
      glowGradient.addColorStop(1, "rgba(130, 70, 180, 0)");

      ctx.fillStyle = glowGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => {
      updateCanvasSize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      window.removeEventListener("resize", handleResize);
      if (ctx && canvas) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [fly, altitudeAnimationInProgress, prepareDraw, altitude, rocketRef]);

  return (
    <div
      style={{
        position: "fixed", // ✅ FIXED to viewport
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        pointerEvents: "none",
        zIndex: 0, // Behind everything
        opacity: getIntensity() > 0 ? 1 : 0,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          mixBlendMode: "screen",
        }}
      />
    </div>
  );
});

// Main Rocket component with sprite animation
function Rocket() {
  const [fly, setFly] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const rocketRef = useRef();
  const pivotRef = useRef();
  const initialYPosition = useRef(0);
  const frameTimeRef = useRef(0);
  const startTimeRef = useRef(performance.now() / 1000);
  const animationFrameRef = useRef();

  const gsapAnimationsRef = useRef([]);

  const {
    prepareDraw,
    altitude,
    history,
    altitudeAnimationInProgress,
    betClosed,
  } = useAppContext();

  const imageFrames = useMemo(() => {
    return preloadImages();
  }, []);

  const flyPositionProps = {
    x: 300,
    y: -30,
    rotateX: -5.73,
    rotateZ: -11.46 + 30,
  };
  const flyPositionExtendProps = {
    x: 310,
    y: -80,
    rotateX: -5.73,
    rotateZ: -11.46 + 30,
  };
  const flyAwayPositionProps = {
    x: 1000,
    y: -2000,
    rotateX: -5.73,
    rotateZ: -11.46 + 30,
  };
  const defaultPositionProps = {
    x: 150,
    y: 1500,
    rotateX: -5.73,
    rotateZ: -11.46 + 30,
  };

  const killAllGsapAnimations = () => {
    gsapAnimationsRef.current.forEach((anim) => {
      if (anim && anim.kill) {
        anim.kill();
      }
    });
    gsapAnimationsRef.current = [];
  };

  const defaultPosition = () => {
    if (!pivotRef.current) return;
    killAllGsapAnimations();
    gsap.set(pivotRef.current, {
      x: defaultPositionProps.x,
      y: defaultPositionProps.y,
      rotateX: defaultPositionProps.rotateX,
      rotateZ: defaultPositionProps.rotateZ,
      rotateY: 0,
    });
  };

  const flyPosition = () => {
    if (!pivotRef.current) return;
    killAllGsapAnimations();

    const tl = gsap.timeline();
    gsapAnimationsRef.current.push(tl);

    tl.to(pivotRef.current, {
      x: flyPositionExtendProps.x,
      y: flyPositionExtendProps.y,
      duration: 2,
      ease: "power3.inOut",
    });
    tl.to(pivotRef.current, {
      x: flyPositionProps.x,
      y: flyPositionProps.y,
      duration: 2,
      ease: "power3.inOut",
    });

    const rotAnim = gsap.to(pivotRef.current, {
      rotateX: flyPositionProps.rotateX,
      rotateZ: flyPositionProps.rotateZ,
      rotateY: 0,
      duration: 2.4,
    });
    gsapAnimationsRef.current.push(rotAnim);
  };

  const preparePosition = () => {
    if (!pivotRef.current) return;
    killAllGsapAnimations();

    const anim1 = gsap.to(pivotRef.current, {
      x: defaultPositionProps.x,
      y: defaultPositionProps.y,
      duration: 1,
      ease: "power2.inOut",
    });
    const anim2 = gsap.to(pivotRef.current, {
      rotateX: defaultPositionProps.rotateX,
      rotateZ: defaultPositionProps.rotateZ,
      rotateY: 0,
      duration: 1,
      ease: "power2.inOut",
    });
    gsapAnimationsRef.current.push(anim1, anim2);
  };

  const flyAwayPosition = () => {
    if (!pivotRef.current) return;
    killAllGsapAnimations();

    const anim = gsap.to(pivotRef.current, {
      x: flyAwayPositionProps.x,
      y: flyAwayPositionProps.y,
      duration: 0.5,
      ease: "power1.in",
    });
    gsapAnimationsRef.current.push(anim);
  };

  const flyAndFlyAwayPosition = () => {
    if (!pivotRef.current) return;
    killAllGsapAnimations();

    const anim1 = gsap.to(pivotRef.current, {
      x: flyAwayPositionProps.x,
      y: flyAwayPositionProps.y,
      duration: 3,
      ease: "power4.inOut",
    });
    const anim2 = gsap.to(pivotRef.current, {
      rotateX: flyAwayPositionProps.rotateX,
      rotateZ: flyAwayPositionProps.rotateZ,
      rotateY: 0,
      duration: 3,
      ease: "power4.inOut",
    });
    gsapAnimationsRef.current.push(anim1, anim2);
  };

  useEffect(() => {
    if (imageFrames.length === 0) return;

    const animate = () => {
      const frameRate = 60;
      const currentTime = performance.now() / 1000 - startTimeRef.current;

      if (currentTime - frameTimeRef.current > 1 / frameRate) {
        const frameIndex =
          Math.floor(currentTime * frameRate) % imageFrames.length;
        setCurrentFrame(frameIndex);
        frameTimeRef.current = currentTime;
      }

      if (rocketRef.current) {
        const bobbingY = Math.sin(currentTime * 1.5) * 0.5;
        rocketRef.current.style.transform = `translateY(${bobbingY * 100}px)`;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [imageFrames]);

  useEffect(() => {
    if (rocketRef.current && initialYPosition.current === 0) {
      initialYPosition.current = 0;
    }
  }, []);

  useEffect(() => {
    if (pivotRef.current) {
      if (prepareDraw || altitude || betClosed) {
        preparePosition();
      } else {
        defaultPosition();
      }

      if (!prepareDraw && altitude) {
        if (altitude < 1.21) {
          flyAndFlyAwayPosition();
        } else {
          flyPosition();
        }
        setFly(true);
      }
    }
  }, [altitude, prepareDraw]);

  useEffect(() => {
    if (!altitudeAnimationInProgress && fly) {
      flyAwayPosition();
    }
  }, [altitudeAnimationInProgress]);

  useEffect(() => {
    setFly(false);
    defaultPosition();
  }, [history]);

  useEffect(() => {
    return () => {
      killAllGsapAnimations();
    };
  }, []);

  if (imageFrames.length === 0) {
    return null;
  }

  return (
    <>
      {/* ✅ Booster light rendered OUTSIDE rocket, fixed to viewport */}
      <RocketBoosterLight fly={fly} rocketRef={rocketRef} />

      <div
        ref={pivotRef}
        className="rocket-pivot"
        style={{
          position: "absolute",
          left: "67%",
          top: "50%",
          willChange: "transform",
        }}
      >
        <div
          ref={rocketRef}
          className="rocket-group"
          style={{
            position: "relative",
          }}
        >
          <img
            src={imageFrames[currentFrame]?.src}
            alt="rocket"
            className="rocket-sprite"
            style={{
              position: "relative",
              width: "1072px",
              height: "605px",
              marginLeft: "-200px",
              marginTop: "150px",
              imageRendering: "high-quality",
              zIndex: 1,
              display: "block",
            }}
          />
        </div>
      </div>
    </>
  );
}

export default Rocket;
